import { formatStoredUsPhoneForInput, normalizeUsPhone } from './phone';

export interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface MemberProfile {
  memberId: string;
  username: string;
  email: string;
  name: string;
  companyName: string;
  website: string;
  phoneNumber: string;
  emailOptIn: boolean;
  isReseller: boolean;
  emailVerified: boolean;
  status: string;
  supportNotes: string;
  serviceCancelledOn: string;
  groups: string[];
}

export class CognitoRequestError extends Error {
  code: string;

  constructor(message: string, code = 'UnknownError') {
    super(message);
    this.name = 'CognitoRequestError';
    this.code = code;
  }
}

interface CognitoAuthenticationResult {
  AccessToken: string;
  IdToken: string;
  RefreshToken?: string;
}

interface CognitoAuthenticationResponse {
  AuthenticationResult?: CognitoAuthenticationResult;
  ChallengeName?: string;
  Session?: string;
}

export class NewPasswordRequiredError extends Error {
  email: string;
  session: string;

  constructor(email: string, session: string) {
    super('Please set a permanent password to finish your account setup.');
    this.name = 'NewPasswordRequiredError';
    this.email = email;
    this.session = session;
  }
}

const region = process.env.REACT_APP_COGNITO_REGION || 'us-east-1';
const clientId = process.env.REACT_APP_HOGCLOUD_MEMBERS_USER_POOL_CLIENT_ID || '';
const endpoint = `https://cognito-idp.${region}.amazonaws.com/`;

const assertConfigured = () => {
  if (!clientId) {
    throw new Error('Hogcloud member authentication is not configured.');
  }
};

const cognitoRequest = async <T,>(
  operation: string,
  body: Record<string, unknown>,
): Promise<T> => {
  assertConfigured();

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json();

  if (!response.ok) {
    const rawCode = payload.__type || payload.code || payload.Code || 'UnknownError';
    const code = String(rawCode).split('#').pop() || 'UnknownError';
    throw new CognitoRequestError(
      payload.message || payload.Message || 'Authentication request failed.',
      code,
    );
  }

  return payload as T;
};

export const signUp = async (
  email: string,
  password: string,
  name: string,
  phoneNumber: string,
) => {
  const attributes = [
    { Name: 'email', Value: email.trim().toLowerCase() },
  ];

  if (name.trim()) {
    attributes.push({ Name: 'name', Value: name.trim() });
  }
  if (phoneNumber.trim()) {
    attributes.push({ Name: 'phone_number', Value: normalizeUsPhone(phoneNumber) });
  }

  return cognitoRequest<{ UserConfirmed: boolean; UserSub: string }>('SignUp', {
    ClientId: clientId,
    Username: email.trim().toLowerCase(),
    Password: password,
    UserAttributes: attributes,
  });
};

export const confirmSignUp = (email: string, confirmationCode: string) =>
  cognitoRequest<void>('ConfirmSignUp', {
    ClientId: clientId,
    Username: email.trim().toLowerCase(),
    ConfirmationCode: confirmationCode.trim(),
  });

export const resendConfirmationCode = (email: string) =>
  cognitoRequest<void>('ResendConfirmationCode', {
    ClientId: clientId,
    Username: email.trim().toLowerCase(),
  });

export const requestPasswordReset = (email: string) =>
  cognitoRequest<void>('ForgotPassword', {
    ClientId: clientId,
    Username: email.trim().toLowerCase(),
  });

export const confirmPasswordReset = (
  email: string,
  confirmationCode: string,
  password: string,
) =>
  cognitoRequest<void>('ConfirmForgotPassword', {
    ClientId: clientId,
    Username: email.trim().toLowerCase(),
    ConfirmationCode: confirmationCode.trim(),
    Password: password,
  });

export const authenticate = async (
  email: string,
  password: string,
): Promise<CognitoTokens> => {
  const username = email.trim().toLowerCase();
  const response = await cognitoRequest<CognitoAuthenticationResponse>('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: clientId,
    AuthParameters: {
      USERNAME: username,
      PASSWORD: password,
    },
  });

  if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
    throw new NewPasswordRequiredError(username, response.Session || '');
  }
  if (!response.AuthenticationResult) {
    throw new Error('Authentication did not return a usable session.');
  }

  return {
    accessToken: response.AuthenticationResult.AccessToken,
    idToken: response.AuthenticationResult.IdToken,
    refreshToken: response.AuthenticationResult.RefreshToken || '',
  };
};

export const completeNewPassword = async (
  email: string,
  newPassword: string,
  session: string,
): Promise<CognitoTokens> => {
  const response = await cognitoRequest<CognitoAuthenticationResponse>('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: clientId,
    Session: session,
    ChallengeResponses: {
      USERNAME: email.trim().toLowerCase(),
      NEW_PASSWORD: newPassword,
    },
  });

  if (!response.AuthenticationResult) {
    throw new Error('Password setup did not return a usable session.');
  }

  return {
    accessToken: response.AuthenticationResult.AccessToken,
    idToken: response.AuthenticationResult.IdToken,
    refreshToken: response.AuthenticationResult.RefreshToken || '',
  };
};

export const refreshAuthentication = async (
  refreshToken: string,
): Promise<CognitoTokens> => {
  const response = await cognitoRequest<{
    AuthenticationResult: CognitoAuthenticationResult;
  }>('InitiateAuth', {
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: clientId,
    AuthParameters: {
      REFRESH_TOKEN: refreshToken,
    },
  });

  return {
    accessToken: response.AuthenticationResult.AccessToken,
    idToken: response.AuthenticationResult.IdToken,
    refreshToken,
  };
};

export const getProfile = async (accessToken: string): Promise<MemberProfile> => {
  const response = await cognitoRequest<{
    Username: string;
    UserAttributes: Array<{ Name: string; Value: string }>;
  }>('GetUser', {
    AccessToken: accessToken,
  });
  const attributes = Object.fromEntries(
    response.UserAttributes.map(({ Name, Value }) => [Name, Value]),
  );

  return {
    memberId: attributes.sub || response.Username,
    username: response.Username,
    email: attributes.email || '',
    name: attributes.name || '',
    companyName: '',
    website: '',
    phoneNumber: formatStoredUsPhoneForInput(attributes.phone_number || ''),
    emailOptIn: true,
    isReseller: false,
    emailVerified: attributes.email_verified === 'true',
    status: 'ACTIVE',
    supportNotes: '',
    serviceCancelledOn: '',
    groups: [],
  };
};

export const updateProfile = (
  accessToken: string,
  profile: Pick<MemberProfile, 'name' | 'phoneNumber'>,
) => {
  const attributes: Array<{ Name: string; Value: string }> = [];

  if (profile.name.trim()) {
    attributes.push({ Name: 'name', Value: profile.name.trim() });
  }
  if (profile.phoneNumber.trim()) {
    attributes.push({
      Name: 'phone_number',
      Value: normalizeUsPhone(profile.phoneNumber),
    });
  }
  if (!attributes.length) {
    return Promise.resolve();
  }

  return cognitoRequest<void>('UpdateUserAttributes', {
    AccessToken: accessToken,
    UserAttributes: attributes,
  });
};

export const globalSignOut = (accessToken: string) =>
  cognitoRequest<void>('GlobalSignOut', {
    AccessToken: accessToken,
  });

export const getTokenExpiration = (token: string): number => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload));
    return Number(decoded.exp || 0) * 1000;
  } catch {
    return 0;
  }
};

export const getTokenGroups = (token: string): string[] => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload));
    const groups = decoded['cognito:groups'];
    return Array.isArray(groups) ? groups.map(String) : [];
  } catch {
    return [];
  }
};

export const cognitoStorageKey = `hogcloud-members-session-${clientId || 'unconfigured'}`;
